import { FaFacebookF, FaInstagram, FaLinkedinIn, FaXTwitter, FaYoutube } from "react-icons/fa6";
import logoImage from "../assets/loanInMinutes/Group.png";
import { NbfcLink } from "./NbfcLink";
import { CUSTOMER_SUPPORT_HOURS, VOICE_SUPPORT_HOURS } from "../utils/supportHours";

const quickLinks = [
  { label: "Home", href: "/" },
  { label: "Apply Now", href: "/apply-now" },
  { label: "How it Works", href: "/#how-it-works" },
  { label: "Loan Calculator", href: "/#loan-calculator" },
  { label: "Contact Us", href: "/contact-us" },
  { label: "About Us", href: "/#about-us" },
  { label: "FAQ", href: "/#faq" },
];

const legalLinks = [
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms and Conditions", href: "/terms-conditions" },
  {label: "Fair Practice Code", href: "/fair-practice-code" },
  {label: "Grievance Redressal Policy", href: "/grievance-redressal-policy" },
];

// An icon only shows once the brand's own profile URL is set in the env;
// a site's bare homepage (e.g. https://www.linkedin.com/) doesn't count.
const socialLinks = [
  { label: "Facebook", href: import.meta.env.VITE_COMPANY_FACEBOOK_URL, Icon: FaFacebookF },
  { label: "Instagram", href: import.meta.env.VITE_COMPANY_INSTAGRAM_URL, Icon: FaInstagram },
  { label: "LinkedIn", href: import.meta.env.VITE_COMPANY_LINKEDIN_URL, Icon: FaLinkedinIn },
  { label: "X", href: import.meta.env.VITE_COMPANY_X_URL, Icon: FaXTwitter },
  { label: "YouTube", href: import.meta.env.VITE_COMPANY_YOUTUBE_URL, Icon: FaYoutube },
].filter((social) => /^https?:\/\/[^/]+\/.+/.test(social.href || ""));

const Footer = () => {
  return (
    <footer id="contact" className="w-full bg-[#fdf5f3] border-t-4 border-[#CC1A1A] scroll-mt-[73px]">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto  px-6 lg:px-8 py-12 lg:py-14">
        {/* Mobile: 2-col grid — brand + contact span full width, links side by side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">

          {/* Column 1 — Brand (full width on mobile) */}
          <div className="col-span-2 lg:col-span-1 flex flex-col items-center lg:items-start gap-5">
            <a href="/">
              <img src={logoImage} alt="Loan In Seconds" className="h-15 sm:h-12 object-contain" />
            </a>
            <p className="text-gray-900 text-base sm:text-sm font-medium leading-relaxed text-center lg:text-left">
              We help working professionals access faster financial support through a simple, secure, and transparent digital lending experience.
            </p>
            {/* Social icons */}
            {socialLinks.length > 0 && (
              <div className="flex items-center justify-center lg:justify-start gap-3 mt-1">
                {socialLinks.map(({ label, href, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="w-9 h-9 rounded-full bg-[#CC1A1A] flex items-center justify-center text-white text-sm hover:bg-red-800 transition-colors"
                  >
                    <Icon />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Column 2 — Quick Links */}
          <div className="col-span-1 flex flex-col gap-4">
            <h4 className="font-extrabold text-[#CC1A1A] text-xl mb-1 pb-1 border-b-2 border-[#CC1A1A] w-fit">Quick Links</h4>
            {quickLinks.map((link) => (
              <a key={link.label} href={link.href}
                className="text-gray-900 text-base font-semibold hover:text-[#CC1A1A] transition-colors">
                {link.label}
              </a>
            ))}
          </div>

          {/* Column 3 — Legal */}
          <div className="col-span-1 flex flex-col gap-4">
            <h4 className="font-extrabold text-[#CC1A1A] text-xl mb-1 pb-1 border-b-2 border-[#CC1A1A] w-fit">Legal</h4>
            {legalLinks.map((link) => (
              <a key={link.label} href={link.href}
                className="text-gray-900 text-base font-semibold hover:text-[#CC1A1A] transition-colors">
                {link.label}
              </a>
            ))}
          </div>

          {/* Column 4 — Contact Us (full width on mobile) */}
          <div className="col-span-2 lg:col-span-1 flex flex-col gap-5">
            <h4 className="font-extrabold text-[#CC1A1A] text-xl pb-1 border-b-2 border-[#CC1A1A] w-fit">Contact Us</h4>
            <div className="flex items-center gap-3">

              <svg width="25" height="25" viewBox="0 0 19 19" className="flex-shrink-0" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.3221 3.06445H3.06444C2.21822 3.06445 1.53223 3.75045 1.53223 4.59666V13.7899C1.53223 14.6361 2.21822 15.3221 3.06444 15.3221H15.3221C16.1683 15.3221 16.8543 14.6361 16.8543 13.7899V4.59666C16.8543 3.75045 16.1683 3.06445 15.3221 3.06445Z" stroke="#4D4D4D" stroke-width="1.53221" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M16.8543 5.36279L9.98236 9.72959C9.74584 9.87777 9.47237 9.95636 9.19327 9.95636C8.91416 9.95636 8.6407 9.87777 8.40418 9.72959L1.53223 5.36279" stroke="#4D4D4D" stroke-width="1.53221" stroke-linecap="round" stroke-linejoin="round" />
              </svg>

              <a href={`mailto:${import.meta.env.VITE_COMPANY_EMAIL}`} className="text-gray-900 text-lg sm:text-base font-semibold break-all hover:text-[#CC1A1A] transition-colors">{import.meta.env.VITE_COMPANY_EMAIL}</a>
            </div>
            <div className="flex items-center gap-3">
              <svg width="25" height="25" viewBox="0 0 19 19" className="flex-shrink-0" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_23_1884)">
                  <path d="M16.8543 12.9626V15.2609C16.8552 15.4743 16.8115 15.6854 16.726 15.8809C16.6405 16.0764 16.5151 16.2519 16.3579 16.3962C16.2007 16.5404 16.0151 16.6502 15.813 16.7186C15.6108 16.7869 15.3967 16.8123 15.1842 16.7931C12.8268 16.5369 10.5623 15.7314 8.5727 14.4412C6.72166 13.2649 5.15231 11.6956 3.97608 9.84453C2.68135 7.84592 1.87561 5.57043 1.62414 3.20241C1.60499 2.99056 1.63017 2.77704 1.69807 2.57545C1.76596 2.37386 1.87509 2.18861 2.0185 2.03151C2.16191 1.87441 2.33647 1.74889 2.53105 1.66294C2.72563 1.57699 2.93597 1.5325 3.14869 1.5323H5.447C5.81879 1.52864 6.17923 1.6603 6.46114 1.90274C6.74304 2.14517 6.92717 2.48185 6.97921 2.85C7.07621 3.58551 7.25612 4.30769 7.51548 5.00275C7.61855 5.27696 7.64086 5.57497 7.57976 5.86146C7.51866 6.14796 7.37671 6.41094 7.17073 6.61923L6.19778 7.59219C7.28837 9.51016 8.87643 11.0982 10.7944 12.1888L11.7674 11.2159C11.9757 11.0099 12.2386 10.8679 12.5251 10.8068C12.8116 10.7457 13.1096 10.768 13.3838 10.8711C14.0789 11.1305 14.8011 11.3104 15.5366 11.4074C15.9087 11.4599 16.2486 11.6473 16.4916 11.9341C16.7345 12.2208 16.8636 12.5869 16.8543 12.9626Z" stroke="#4D4D4D" stroke-width="1.53221" stroke-linecap="round" stroke-linejoin="round" />
                </g>
                <defs>
                  <clipPath id="clip0_23_1884">
                    <rect width="18.3865" height="18.3865" fill="white" />
                  </clipPath>
                </defs>
              </svg>

              <a href={`tel:${import.meta.env.VITE_COMPANY_PHONE}`} className="text-gray-900 text-lg sm:text-base font-semibold hover:text-[#CC1A1A] transition-colors">{import.meta.env.VITE_COMPANY_PHONE}</a>
            </div>
            <div className="flex items-start gap-3">
              <svg width="25" height="25" viewBox="0 0 19 19" className="flex-shrink-0" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.3221 7.66106C15.3221 11.4862 11.0787 15.47 9.65372 16.7003C9.52097 16.8001 9.35938 16.8541 9.19329 16.8541C9.0272 16.8541 8.86561 16.8001 8.73286 16.7003C7.3079 15.47 3.06445 11.4862 3.06445 7.66106C3.06445 6.03559 3.71017 4.4767 4.85955 3.32732C6.00893 2.17794 7.56782 1.53223 9.19329 1.53223C10.8188 1.53223 12.3776 2.17794 13.527 3.32732C14.6764 4.4767 15.3221 6.03559 15.3221 7.66106Z" stroke="#4D4D4D" stroke-width="1.53221" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M9.19333 9.95942C10.4627 9.95942 11.4916 8.93043 11.4916 7.66111C11.4916 6.39178 10.4627 5.36279 9.19333 5.36279C7.92401 5.36279 6.89502 6.39178 6.89502 7.66111C6.89502 8.93043 7.92401 9.95942 9.19333 9.95942Z" stroke="#4D4D4D" stroke-width="1.53221" stroke-linecap="round" stroke-linejoin="round" />
              </svg>

              <span className="text-gray-900 text-lg sm:text-base font-semibold">{import.meta.env.VITE_COMPANY_ADDRESS}</span>
            </div>
            <div className="text-gray-900 text-sm flex flex-col gap-1">
              <p><span className="font-bold text-[#CC1A1A]">Voice Support:</span> <span className="font-semibold">{VOICE_SUPPORT_HOURS}</span></p>
              <p><span className="font-bold text-[#CC1A1A]">Customer Support:</span> <span className="font-semibold">{CUSTOMER_SUPPORT_HOURS}</span></p>
            </div>
          </div>

        </div>
      </div>

      {/* NBFC / Powered by bar */}
      
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex flex-col items-center lg:flex-row lg:justify-between gap-1 lg:gap-2 text-center lg:text-left font-semibold border-y border-[#f6bdbd]">
          <span className="text-gray-700 text-lg sm:text-base">
            NBFC Partner : <NbfcLink className="font-bold text-[#CC1A1A] underline hover:text-red-800" />
          </span>
          <span className="text-gray-700 text-lg sm:text-base">
            Powered by : <span className="font-bold text-gray-900">{import.meta.env.VITE_COMPANY_POWERED_BY}</span>
          </span>
        </div>
     

      {/* Copyright bar */}
   
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex flex-col items-center lg:flex-row lg:justify-between gap-1 lg:gap-2 text-center lg:text-left font-medium">
          <span className="text-gray-900 text-base sm:text-sm">© 2026 <NbfcLink className="hover:underline" /> All rights reserved.</span>
          <span className="text-gray-900 text-base sm:text-sm">Loans are subject to credit approval. <a href="/terms-conditions" className="font-bold text-[#CC1A1A] underline hover:text-red-800">Terms and conditions</a> apply.</span>
        </div>
    
    </footer>
  );
};

export default Footer;
